import { TableRow } from '@sam/components';
import { Dispatch, SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  InventoryCategory,
  InventoryCheck,
  InventoryEntry,
  InventoryGroup,
  InventroyMetaInformation,
  Language,
  Office,
  SimpleUser,
} from 'shared';
import i18n from '../../i18n/i18n';
import { getUserNameForSimpleUser } from '../user/User.utils';
import { uid } from 'uid';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Util method to generate tableRows for inventoryCategories
 * @param categories the categories to display
 * @param navigate naviagateFunction
 * @returnsArray of TableRows
 */
export const generateTableRowsForInventoryCategories = (
  categories: InventoryCategory[],
  navigate: NavigateFunction,
): TableRow[] => {
  return categories.map((category) => ({
    id: category.id,
    actions: [
      <IconComponent
        type={IconType.EDIT}
        width={30}
        onClick={() => navigate('edit', { state: { category } })}
      />,
    ],
    content: [
      category.title,
      i18n.t(`general.inventoryGroup.${category.group}`),
      category.lastUpdated.toLocaleDateString('DE-de'),
    ],
  }));
};

/**
 * Util method to generate an empty InventoryCategory
 * @param override partial category to djust created instance
 * @returns generated InventoryCategory
 */
export const generateEmptyCategory = (
  override?: Partial<InventoryCategory>,
): InventoryCategory => ({
  createDate: undefined!,
  createdBy: undefined!,
  informations: [],
  group: InventoryGroup.ITEM,
  checkPeriod: 12,
  id: undefined!,
  lastUpdated: undefined!,
  title: '',
  translations: new Map<Language, string>(),
  updatedBy: '',
  ...override,
});

/**
 * Util method to convert inventoryMetaInformations into tableRows
 * @param infos the entries to display
 * @param setInfoToEdit handler to select an entry
 * @returns  Array of TableRows
 */
export const convertMetaInformationIntoTableRows = (
  infos: InventroyMetaInformation[],
  setInfoToEdit: Dispatch<SetStateAction<InventroyMetaInformation | undefined>>,
): TableRow[] => {
  return infos.map((information) => ({
    id: information.id,
    onClick: () => setInfoToEdit(information),
    actions: [
      <IconComponent
        type={IconType.EDIT}
        width={30}
        className="clickable"
        onClick={() => setInfoToEdit(information)}
      />,
    ],
    content: [information.title, information.type],
  }));
};

/**
 * Util method to gernerate TableRows for inventoryEntry checks
 * @param checks the checks to display
 * @param users list of all users to show createdBy
 * @param setEntryToEdit Select entry to update
 * @param setEntryToDelete select entry to delete
 * @returns  Array of TableRows
 */
export const convertEntryChecksIntoTableEntries = (
  checks: InventoryCheck[],
  users: SimpleUser[],
  setEntryToEdit: Dispatch<SetStateAction<InventoryCheck | undefined>>,
  setEntryToDelete: Dispatch<SetStateAction<string | undefined>>,
): TableRow[] => {
  return checks.map((check) => ({
    id: check.id,
    actions: [
      <IconComponent type={IconType.EDIT} width={30} onClick={() => setEntryToEdit(check)} />,
      <IconComponent
        type={IconType.DELETE}
        width={30}
        onClick={() => setEntryToDelete(check.id)}
      />,
    ],
    content: [
      check.createDate.toLocaleDateString('DE-de'),
      getUserNameForSimpleUser(check.createdBy, users),
      check.passed ? i18n.t('pages.inventory.success') : i18n.t('pages.inventory.failure'),
      check.comment,
    ],
  }));
};

/**
 * Util method to generate an inventoryCheck
 * @param createdBy the userId of the user to craete the check
 * @param override partial check to adjust generated instance
 * @returns generated inventoryCheck
 */
export const generateEmptyInventoryCheck = (
  createdBy: string,
  override?: Partial<InventoryCheck>,
): InventoryCheck => ({
  id: uid(),
  comment: '',
  createDate: new Date(),
  createdBy,
  passed: false,
  ...override,
});

/**
 * Util method to convertInventoryEntries int oTableRows
 * @param items the items to display
 * @param categories available InventoryCategories
 * @param navigate used for edit method
 * @param offices list of all offices
 * @param setEntryToAddCheck used to quickly add a check
 * @returns  Array of TableRows
 */
export const convertInventoryEntriesIntoTableEntries = (
  items: InventoryEntry[],
  categories: InventoryCategory[],
  navigate: NavigateFunction,
  offices: Office[],
  users: SimpleUser[],
  selectedCategories: string[],
  setEntryToAddCheck: Dispatch<SetStateAction<InventoryEntry | undefined>>,
): TableRow[] => {
  return items.map((entry) => {
    const user: string = getUserNameForSimpleUser(entry.currentUserId, users);
    const content: string[] = [];
    if (entry.group === InventoryGroup.CONTRACT) {
      content.push(
        entry.name,
        categories.find((cat) => cat.id === entry.categoryId)?.title || '',
        user,
        offices.find((office) => office.id === entry.officeId)?.name || '-',
        entry.contractPartner || '-',
        entry.date?.toLocaleDateString('DE-de') || '-',
        entry.disabled ? i18n.t('pages.inventory.deactivated') : i18n.t('pages.inventory.active'),
      );
    } else {
      const lastCheck: Date | undefined = entry.checks.sort(
        (checkA, checkB) =>
          checkB.createDate.getMilliseconds() - checkA.createDate.getMilliseconds(),
      )[0]?.createDate;
      content.push(
        entry.name,
        categories.find((cat) => cat.id === entry.categoryId)?.title || '',
        entry.number.toString().padStart(5, '0'),
        user,
        lastCheck?.toLocaleDateString('DE-de') || '-',
        entry?.stationNumber || '-',
        offices.find((office) => office.id === entry.officeId)?.name || '-',
        entry.disabled ? i18n.t('pages.inventory.deactivated') : i18n.t('pages.inventory.active'),
      );
    }
    if (entry.group === InventoryGroup.TOOL)
      content.push(
        entry.toolingGroup ? i18n.t(`pages.inventory.toolingGroup.${entry.toolingGroup}`) : '-',
        entry.toolType ? i18n.t(`pages.inventory.toolingType.${entry.toolType}`) : '-',
      );
    return {
      id: entry.id,
      onClick: () =>
        navigate('/inventory/edit', {
          state: { item: entry, selectedCategories },
        }),
      actions: [
        <IconComponent
          type={IconType.SEARCH}
          width={30}
          onClick={(evt) => {
            evt.stopPropagation();
            setEntryToAddCheck(entry);
          }}
        />,
      ],
      content,
    };
  });
};
